import {AsYouType} from 'libphonenumber-js';
import {Icon, Show, Space, VStack} from 'platform/foundation';

import {Fragment, useId, useMemo, useRef} from 'react';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {DropdownDivider} from '../Dropdown/components/DropdownDivider';
import {DropdownItem} from '../Dropdown/components/DropdownItem';
import {Dropdown} from '../Dropdown/Dropdown';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {PlatformInputElement} from '../TextInput/components/PlatformInputElement';
import {PlatformInputWrapper} from '../TextInput/components/PlatformInputWrapper';
import {dialCodePrefix, flagIcons, FlagIcons} from './constants';
import {Country} from './types/Country';
import {PhoneNumber} from './types/PhoneNumber';
import {
  Alpha3CountryCode,
  getAlpha2CodeByAlpha3code,
  getAlpha3CodeByAlpha2code,
} from './utils/countryCodes';
import {getCountryCodeFromNumber} from './utils/getCountryCodeFromNumber';
import {getDefaultCountryCode} from './utils/getDefaultCountryCode';
import {getDefaultInnerValue} from './utils/getDefaultInnerValue';
import {getFormattedNumber} from './utils/getFormattedNumber';
import {getNationalNumber} from './utils/getNationalNumber';
import {getPhoneNumberPlaceholder} from './utils/getPhoneNumberPlaceholder';
import {sortCountries} from './utils/sortCountries';

export interface PhoneInputProps
  extends FormControlProps<PhoneNumber, HTMLInputElement, [string]>, TestIdProps {
  /**
   * @about
   * The default country code of default selected country.
   */
  defaultCountry?: Alpha3CountryCode;
  /**
   * @about
   * Preferred countries will be displayed at the top of dropdown with countries.
   */
  preferredCountries?: Alpha3CountryCode[];
  /**
   * @about
   * The countries data that contains name, country code and country dialing code about each country.
   */
  countries?: Country[];
  placeholder?: string;
  /**
   * @about
   * Set `true` for "national" phone number format.
   * Leave `undefined` for "international" phone number format.
   * By default it's international.
   */
  isNational?: true;
  label?: string | Nullish;
  /**
   * @about
   * The custom icons for country flags.
   */
  flagIcons?: FlagIcons;
  isRecommended?: boolean;
}

export function PhoneInput(props: PhoneInputProps) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  const setInputRef = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    element?.classList.add(PHONE_INPUT_FONT_SIZE_CLASS);
  };

  const defaultedCountryCode =
    props.value?.countryCode ??
    getDefaultCountryCode(props.defaultCountry, props.preferredCountries, props.countries);

  const defaultedValue = getDefaultInnerValue(defaultedCountryCode, props.value, props.isNational);

  const countriesData = useMemo(
    (): Country[] | undefined => sortCountries(props.preferredCountries, props.countries),
    [props.countries, props.preferredCountries]
  );

  const getFlagIcon = (countryCode: Alpha3CountryCode) => {
    const flagIcon = (props.flagIcons ?? flagIcons)?.[countryCode];
    if (!flagIcon) {
      return countryCode;
    }

    return <Icon value={flagIcon} />;
  };

  const onValueChange = (value: string | null) => {
    const countryCode =
      getCountryCodeFromNumber(value ?? '', countriesData) ?? defaultedCountryCode;
    const formattedNumber = getFormattedNumber(value ?? '', countryCode);

    provideChanges(formattedNumber, countryCode);

    if (!value) {
      return;
    }

    const oldIndex = inputRef.current?.selectionStart ?? 0;
    const oldValue = inputRef.current?.value ?? '';
    const isCursorAtTheEnd = oldValue.length <= oldIndex;
    const oldLength = oldValue.substring(0, oldIndex).length;

    window.requestAnimationFrame(() => {
      if (!inputRef.current) {
        return;
      }

      const currentValue = inputRef.current.value;
      const oldHasPrefix = oldValue.startsWith('+');
      const newHasPrefix = currentValue.startsWith('+');

      if (!oldHasPrefix && newHasPrefix) {
        inputRef.current.setSelectionRange(currentValue.length, currentValue.length);
        return;
      }

      const newLength = currentValue.substring(0, oldIndex).length;
      const newIndex = isCursorAtTheEnd
        ? currentValue.length
        : Math.max(0, Math.min(newLength - oldLength + oldIndex, currentValue.length));

      inputRef.current.setSelectionRange(newIndex, newIndex);
    });
  };

  const onSelectCountry = (country: Country) => {
    const phoneNumberValue = getNationalNumber(props.value?.number ?? '');
    const newInnerValue = props.isNational ? phoneNumberValue : country.dialCode + phoneNumberValue;
    const formattedNumber = getFormattedNumber(newInnerValue, country.countryCode);

    provideChanges(formattedNumber, country.countryCode);
  };

  const provideChanges = (phoneNumber: string, country: Alpha3CountryCode) => {
    if (!props.onChange) {
      return;
    }

    const alpha2Code = getAlpha2CodeByAlpha3code(country);
    const asYouType = new AsYouType(alpha2Code);
    asYouType.input(phoneNumber);

    const number = asYouType.getNumber();
    const countryCode = number?.country ? getAlpha3CodeByAlpha2code(number.country) : country;

    props.onChange?.(
      {
        countryCode,
        number: number?.nationalNumber.toString() ?? '',
        prefix: number ? `${dialCodePrefix}${number.countryCallingCode}` : phoneNumber,
      },
      undefined,
      number?.number.toString() ?? phoneNumber
    );
  };

  const isInvalid = props.isInvalid || !!props.errorMessage;

  return (
    <VStack>
      <Label
        id={id}
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        isRecommended={props.isRecommended}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <PlatformInputWrapper
        isDisabled={props.isDisabled}
        isInvalid={isInvalid}
        isRecommended={props.isRecommended}
      >
        <>
          <Show when={countriesData}>
            <Dropdown
              isHeightLimited
              isOpen={props.isDisabled ? false : undefined}
              dropdownControl={
                <div
                  className="flex cursor-pointer select-none items-center p-1 pe-0 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40"
                  tabIndex={0}
                  data-disabled={props.isDisabled ? '' : null}
                >
                  <span className="me-0 [&_svg]:!w-[22.4px]">
                    {getFlagIcon(defaultedCountryCode)}
                  </span>
                  <span className="text-[length:var(--text-size-x-small)] [&_svg]:!h-4 [&_svg]:!w-4">
                    <Icon value="navigation/arrow_drop_down" size={4} />
                  </span>
                </div>
              }
              data-testid={suffixTestId('phoneDropdown', props)}
            >
              {countriesData?.map((country, index) => (
                <Fragment key={country.countryCode}>
                  <Show
                    when={
                      (props.preferredCountries?.length ?? 0) > 0 &&
                      index === props.preferredCountries?.length
                    }
                  >
                    <DropdownDivider />
                  </Show>
                  <DropdownItem
                    onClick={() => onSelectCountry(country)}
                    prefix={getFlagIcon(country.countryCode)}
                    label={country.name}
                    isActive={country.countryCode === defaultedCountryCode}
                    data-testid={suffixTestId('phoneDropdown', props)}
                  />
                </Fragment>
              ))}
            </Dropdown>
          </Show>
          <Space horizontal={2} />
          <PlatformInputElement
            autoComplete="disabled"
            ref={setInputRef}
            id={id}
            isInvalid={isInvalid}
            isRequired={props.isRequired}
            isDisabled={!!props.isDisabled}
            placeholder={
              props.placeholder ?? getPhoneNumberPlaceholder(defaultedCountryCode, props.isNational)
            }
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onChange={onValueChange}
            value={defaultedValue}
            data-testid={suffixTestId('phoneInput', props)}
          />
        </>
      </PlatformInputWrapper>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}

const PHONE_INPUT_FONT_SIZE_CLASS = '!text-[length:var(--text-size-small)]';
